import { AccessTime } from '@mui/icons-material';
import {
  List,
  ListSubheader,
  Skeleton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import EmptyState from 'components/EmptyState';
import TimelineIcon from 'components/TimelineIcon';
import dayjs from 'dayjs';
import { useTimelineDescription } from 'hooks';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { SummaryTimeline, Timeline, TimelineTypeEnum } from 'types/timeline.types';
import { useRecoilValue } from 'recoil';
import { accountSelectedSelector } from 'atoms';

function TimelineListLoading() {
  return (
    <List
      sx={{
        flex: '1 1 auto',
        p: 0,
        mt: 2,
        overflow: 'auto',
      }}
    >
      <ListSubheader sx={{ bgcolor: 'grey.200' }}>
        <Skeleton />
      </ListSubheader>
      {Array.from({ length: 5 }).map((_, i) => (
        <ListItem key={i}>
          <ListItemAvatar>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemAvatar>
          <ListItemText primary={<Skeleton />} secondary={<Skeleton />} />
        </ListItem>
      ))}
    </List>
  );
}

function TimelineListEmpty() {
  const { t } = useTranslation(['_common', 'events']);
  return (
    <EmptyState
      sx={{ flex: '1 1 auto', p: 0, mt: 2, overflow: 'auto' }}
      title={t('_common:there_is_still_nothing_here')}
      description={t('events:there_is_no_event_for_this_account')}
    />
  );
}

type MountAdditionalSecondaryText = (event: Timeline | SummaryTimeline) => React.ReactNode;

function TimelineListContent({
  events,
  mountAdditionalSecondaryText,
}: {
  events: Timeline[];
  mountAdditionalSecondaryText?: MountAdditionalSecondaryText;
}) {
  const { t } = useTranslation('_common');

  const normalizedEvents = React.useMemo(() => {
    return events
      .map((event) => event.date.slice(0, '2022-06-29'.length))
      .reduce(
        (dates, currentDate) => (!dates.includes(currentDate) ? [...dates, currentDate] : dates),
        [] as string[],
      )
      .map((date) => ({
        date,
        values: events.filter((event) => event.date.slice(0, '2022-06-29'.length) === date),
      }));
  }, [events]);

  return (
    <List
      sx={{
        flex: '1 1 auto',
        p: 0,
        mt: 2,
        overflow: 'auto',
      }}
    >
      {normalizedEvents.map((dateEvents) => (
        <Fragment key={dateEvents.date}>
          <ListSubheader sx={{ bgcolor: 'grey.200' }}>
            {dayjs(dateEvents.date).calendar(dayjs(), {
              sameDay: `[${t('today')}]`,
              lastDay: 'DD MMM',
              nextDay: 'DD MMM',
              lastWeek: 'DD MMM',
              nextWeek: 'DD MMM',
              sameElse: 'DD MMM',
            })}
          </ListSubheader>
          {dateEvents.values.map((event, index) => (
            <Fragment key={event.id}>
              <TimelineListItem {...{ event, mountAdditionalSecondaryText }} />
              {index < dateEvents.values.length - 1 && <Divider variant="middle" />}
            </Fragment>
          ))}
        </Fragment>
      ))}
    </List>
  );
}

function TimelineListItem({
  event,
  mountAdditionalSecondaryText,
}: {
  event: Timeline | SummaryTimeline;
  mountAdditionalSecondaryText?: MountAdditionalSecondaryText;
}) {
  const { title, description } = useTimelineDescription({ event });
  const accountSelected = useRecoilValue(accountSelectedSelector);
  const occurrenceId =
    event.type === TimelineTypeEnum.LOG && event?.event?.occurrenceId
      ? `?occurrenceId=${event.event.occurrenceId}`
      : '';

  return (
    <ListItemButton
      component={Link}
      to={
        accountSelected
          ? `/events/${event.type ?? TimelineTypeEnum.PANIC}/${event.id}${occurrenceId}`
          : `/events/${event.type ?? TimelineTypeEnum.PANIC}/${event.id}/${
              (event as SummaryTimeline).accountId
            }${occurrenceId}`
      }
    >
      <ListItemIcon>
        <TimelineIcon type={event.type ?? TimelineTypeEnum.PANIC} />
      </ListItemIcon>
      <ListItemText
        primary={title}
        secondary={
          <>
            {description && (
              <>
                <span>{description}</span>
                <br />
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <AccessTime sx={{ width: '0.8rem', height: '0.8rem', mr: 0.5 }} />
              {dayjs(event.date).format('LTS')}
              {mountAdditionalSecondaryText?.(event)}
            </Box>
          </>
        }
        secondaryTypographyProps={{
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
        primaryTypographyProps={{
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    </ListItemButton>
  );
}

const TimelineList = {
  Empty: TimelineListEmpty,
  Loading: TimelineListLoading,
  Content: TimelineListContent,
};

export default TimelineList;
